import { useState } from 'react';
import type { Category } from '../../domain/types';
import {
  listCategories,
  reorderCategories,
  restoreCategory,
  saveCategory,
  softDeleteCategory,
} from '../../db/categories';
import { listAll } from '../../db/entries';
import { useAsync } from '../hooks/useAsync';
import { useDataVersion } from '../../db/events';
import { CATEGORY_COLORS, CatIcon, ICON_NAMES } from './CatIcon';
import { shouldSubmitOnEnter } from './CommitInput';

type Draft = { id?: string; name: string; color: string; icon: string; order: number };

export function CategoryManager() {
  const version = useDataVersion();
  const [showDeleted, setShowDeleted] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);

  const active = useAsync<Category[]>(() => listCategories(), [version], []);
  const all = useAsync<Category[]>(() => listCategories(true), [version], []);
  const counts = useAsync<Map<string, number>>(
    async () => {
      const entries = await listAll();
      const m = new Map<string, number>();
      for (const e of entries) {
        if (e.categoryId) m.set(e.categoryId, (m.get(e.categoryId) ?? 0) + 1);
      }
      return m;
    },
    [version],
    new Map(),
  );

  const deleted = all.filter((c) => c.deleted);

  const move = async (index: number, delta: number) => {
    const next = [...active];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    await reorderCategories(next);
  };

  const onDelete = async (c: Category) => {
    const n = counts.get(c.id) ?? 0;
    const msg =
      n > 0
        ? `「${c.name}」を削除します。\nこのカテゴリを使用している ${n} 件の記録は「未設定」と表示されるようになります（記録自体は消えません）。`
        : `「${c.name}」を削除します。`;
    if (!confirm(msg)) return;
    await softDeleteCategory(c.id);
  };

  const onSubmit = async () => {
    if (!draft) return;
    const name = draft.name.trim();
    if (name === '') return;
    await saveCategory({ ...draft, name, deleted: false });
    setDraft(null);
  };

  return (
    <>
      <ul className="cat-list">
        {active.map((c, i) => (
          <li key={c.id}>
            <i className="swatch" style={{ background: c.color }} />
            <CatIcon name={c.icon} />
            <span className="cat-name">{c.name}</span>
            <span className="muted cat-count">{counts.get(c.id) ?? 0}件</span>
            <span className="cat-actions">
              <button className="icon-btn" onClick={() => move(i, -1)} disabled={i === 0} aria-label="上へ">
                ↑
              </button>
              <button
                className="icon-btn"
                onClick={() => move(i, 1)}
                disabled={i === active.length - 1}
                aria-label="下へ"
              >
                ↓
              </button>
              <button className="icon-btn" onClick={() => setDraft({ ...c })} aria-label="編集">
                ✎
              </button>
              <button className="icon-btn danger" onClick={() => onDelete(c)} aria-label="削除">
                ✕
              </button>
            </span>
          </li>
        ))}
      </ul>

      <div className="btn-row">
        <button
          className="ghost-btn"
          onClick={() =>
            setDraft({
              name: '',
              color: CATEGORY_COLORS[active.length % CATEGORY_COLORS.length],
              icon: 'note',
              order: active.length + 1,
            })
          }
        >
          ＋ カテゴリを追加
        </button>
        {deleted.length > 0 && (
          <button className="ghost-btn" onClick={() => setShowDeleted((v) => !v)}>
            削除済み（{deleted.length}）を{showDeleted ? '隠す' : '表示'}
          </button>
        )}
      </div>

      {showDeleted && deleted.length > 0 && (
        <ul className="cat-list deleted-list">
          {deleted.map((c) => (
            <li key={c.id} className="is-deleted">
              <i className="swatch" style={{ background: c.color }} />
              <CatIcon name={c.icon} />
              <span className="cat-name">{c.name}</span>
              <span className="muted cat-count">{counts.get(c.id) ?? 0}件</span>
              <span className="cat-actions">
                <button className="icon-btn" onClick={() => restoreCategory(c.id)}>
                  復元
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}

      {draft && (
        <div className="modal-backdrop" onClick={() => setDraft(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>{draft.id ? 'カテゴリを編集' : 'カテゴリを追加'}</h3>

            <label className="field">
              <span className="field-label">名前</span>
              <input
                type="text"
                maxLength={30}
                autoFocus
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                onKeyDown={(e) => shouldSubmitOnEnter(e) && onSubmit()}
              />
            </label>

            <div className="field">
              <span className="field-label">色</span>
              <div className="swatch-grid">
                {CATEGORY_COLORS.map((col) => (
                  <button
                    key={col}
                    className={`swatch-btn${draft.color === col ? ' is-on' : ''}`}
                    style={{ background: col }}
                    onClick={() => setDraft({ ...draft, color: col })}
                    aria-label={col}
                  />
                ))}
                <input
                  type="color"
                  value={draft.color}
                  onChange={(e) => setDraft({ ...draft, color: e.target.value })}
                  aria-label="カスタムカラー"
                />
              </div>
            </div>

            <div className="field">
              <span className="field-label">アイコン</span>
              <div className="icon-grid">
                {ICON_NAMES.map((n) => (
                  <button
                    key={n}
                    className={`icon-pick${draft.icon === n ? ' is-on' : ''}`}
                    onClick={() => setDraft({ ...draft, icon: n })}
                  >
                    <CatIcon name={n} size={20} />
                  </button>
                ))}
              </div>
            </div>

            <div className="btn-row modal-actions">
              <button className="ghost-btn" onClick={() => setDraft(null)}>
                キャンセル
              </button>
              <button className="primary-btn" onClick={onSubmit} disabled={draft.name.trim() === ''}>
                保存
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
